import EmptyState from '@/components/EmptyState';
import { useLabels } from '@/features/labels/hooks/useLabels';
import {
  useCreatePurgeRule,
  usePurgeRules,
  useUpdatePurgeRule,
} from '@/features/purge/hooks/usePurge';
import { MANAGED_RULE_NAME } from '@/features/purge/purge-settings';
import PurgeAccessGate from '@/features/purge/components/PurgeAccessGate';
import PurgeTargetSection from '@/features/purge/components/PurgeTargetSection';
import { useT } from '@/i18n';
import { useSettingsDetailBackLink } from '@/utils/useMediaQuery';
import type { PurgeRule, PurgeTarget } from '@restaurant/api-contracts';
import {
  Block,
  Button,
  List,
  ListItem,
  Navbar,
  Page,
  Preloader,
  Segmented,
} from 'framework7-react';
import { useState } from 'react';

function PurgeSettingsPage() {
  const t = useT().purge;

  return (
    <PurgeAccessGate title={t.pageTitle}>
      <PurgeSettingsContent />
    </PurgeAccessGate>
  );
}

function PurgeSettingsContent() {
  const t = useT().purge;
  const settingsBackLink = useSettingsDetailBackLink();
  const { data: rules, isLoading, error } = usePurgeRules();
  const { data: labels } = useLabels();
  const createRule = useCreatePurgeRule();
  const updateRule = useUpdatePurgeRule();

  const [target, setTarget] = useState<PurgeTarget>('ORDER');

  const managedFor = (t2: PurgeTarget): PurgeRule | undefined =>
    rules?.find((r) => r.target === t2 && r.name === MANAGED_RULE_NAME[t2]);

  const extraCountFor = (t2: PurgeTarget): number =>
    rules?.filter((r) => r.target === t2 && r.name !== MANAGED_RULE_NAME[t2]).length ?? 0;

  return (
    <Page name="purge-settings">
      <Navbar title={t.pageTitle} backLink={settingsBackLink} />

      {isLoading && (
        <Block className="text-align-center">
          <Preloader />
        </Block>
      )}
      {error && (
        <EmptyState
          icon="exclamationmark_triangle"
          iconColor="#ff9500"
          title={t.couldntLoad}
          description={error instanceof Error ? error.message : undefined}
        />
      )}

      {rules && (
        <>
          <Block>
            <p>{t.intro}</p>
          </Block>

          <Segmented strong tag="p">
            <Button active={target === 'ORDER'} onClick={() => setTarget('ORDER')}>
              {t.orders.title}
            </Button>
            <Button active={target === 'RESERVATION'} onClick={() => setTarget('RESERVATION')}>
              {t.reservations.title}
            </Button>
          </Segmented>

          <PurgeTargetSection
            key={target}
            target={target}
            rule={managedFor(target)}
            extraRuleCount={extraCountFor(target)}
            labels={labels ?? []}
            onCreate={createRule.mutateAsync}
            onUpdate={updateRule.mutateAsync}
          />

          <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
            <ListItem title={t.history.pageTitle} link="/settings/tidy-up/history" />
          </List>
        </>
      )}
    </Page>
  );
}

export default PurgeSettingsPage;
